import { useCallback, useState } from "react";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OverviewPage } from "./pages/Overview";
import { CachePage } from "./pages/Cache";
import { ErrorsPage } from "./pages/Errors";
import { BrandPage } from "./pages/Brand";
import { TesterPage } from "./pages/Tester";
import { LoginPage } from "./pages/Login";
import { BrandsKeyPage } from "./pages/BrandsKey";
import { useHashRoute } from "./hooks/useHashRoute";
import { useLocalStorage } from "./hooks/useLocalStorage";
import type { TimeWindow } from "./types";
import { TIME_WINDOWS } from "./types";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "cache", label: "Cache" },
  { id: "errors", label: "Errors" },
  { id: "brand", label: "Brand" },
  { id: "tester", label: "Tester" },
  { id: "keys", label: "Brands & Keys" },
];

interface PageState {
  loading: boolean;
  lastUpdated: Date | null;
  refetch: () => void;
}

const NOOP_STATE: PageState = {
  loading: false,
  lastUpdated: null,
  refetch: () => {},
};

export const App = () => {
  const [tab, arg, navigate] = useHashRoute("overview");
  const [windowSize, setWindowSize] = useLocalStorage<TimeWindow>(
    "dashboard.window",
    "24h",
  );
  const [token, setToken] = useState<string | null>(localStorage.getItem("dashboard.token"));
  
  
  const [pageState, setPageState] = useState<PageState>(NOOP_STATE);

  const safeWindow: TimeWindow = TIME_WINDOWS.includes(windowSize as TimeWindow)
    ? (windowSize as TimeWindow)
    : "24h";

  // Handle Login
  const handleLogin = (newToken: string) => {
    setToken(newToken);
    navigate("overview");
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem("dashboard.token");
    localStorage.removeItem("dashboard.user");
    setToken(null);
  };

  // Stable identity so child pages don't refetch on every parent render.
  const registerState = useCallback((s: PageState) => {
    setPageState((prev) =>
      prev.loading === s.loading &&
      prev.lastUpdated === s.lastUpdated &&
      prev.refetch === s.refetch
        ? prev
        : s,
    );
  }, []);

  const handleSelectBrand = (brandId: string) => {
    navigate("brand", brandId.toUpperCase());
  };

  const showWindow = tab !== "tester" && tab !== "cache" && tab !== "keys";

  if (!token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Layout
      tabs={TABS}
      activeTab={tab}
      onTabChange={(next) => navigate(next)}
      window={safeWindow}
      onWindowChange={setWindowSize}
      lastUpdated={pageState.lastUpdated}
      loading={pageState.loading}
      onRefresh={() => pageState.refetch()}
      onLogout={handleLogout}
      showWindowSelector={showWindow}
    >
      <ErrorBoundary>
        {tab === "overview" && (
          <OverviewPage
            window={safeWindow}
            onSelectBrand={handleSelectBrand}
            registerState={registerState}
          />
        )}
        {tab === "cache" && <CachePage registerState={registerState} />}
        {tab === "errors" && (
          <ErrorsPage window={safeWindow} registerState={registerState} />
        )}
        {tab === "brand" && (
          <BrandPage
            window={safeWindow}
            brandId={arg}
            onSelectBrand={handleSelectBrand}
            registerState={registerState}
          />
        )}
        {tab === "tester" && <TesterPage />}
        {tab === "keys" && <BrandsKeyPage registerState={registerState} />}
      </ErrorBoundary>
    </Layout>
  );
};

export default App;
